/**
 * MODULE: _shared/frontend-shell/src/components/generative-ui/dataTableCells.ts
 * PURPOSE: Normalize assistant data_result table cells (plain text + CRM link objects).
 * DEPENDENCIES: ../../hooks/assistant/types (DataTableCell, DataTableLinkCell)
 * PUBLIC_API: isDataTableLinkCell, normalizeDataTableCell, normalizeDataTableRows
 * SIDE_EFFECTS: None
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 28/150
 * RECENT: 2026-06-05 [2.2.1288] — link cell normalization for orchestration **`search_contacts`** CRM column (**`/crm/contacts/:id`** href).
 */

import type { DataTableCell } from '../../hooks/assistant/types';

export function isDataTableLinkCell(cell: unknown): cell is { type: 'link'; href: string; label: string } {
  if (!cell || typeof cell !== 'object' || Array.isArray(cell)) return false;
  const c = cell as Record<string, unknown>;
  return c.type === 'link' && typeof c.href === 'string' && typeof c.label === 'string';
}

export function normalizeDataTableCell(cell: unknown): DataTableCell {
  if (isDataTableLinkCell(cell)) {
    return { type: 'link', href: cell.href.trim(), label: cell.label.trim() || 'View' };
  }
  if (cell == null) return '';
  return String(cell);
}

function rowFingerprint(row: DataTableCell[]): string {
  return row.map((cell) => (typeof cell === 'string' ? cell : JSON.stringify(cell))).join('\u001f');
}

/** Drop duplicate table rows (assistant stream occasionally re-emits the same contact row). */
export function dedupeDataTableRows(rows: DataTableCell[][]): DataTableCell[][] {
  const seen = new Set<string>();
  const out: DataTableCell[][] = [];
  for (const row of rows) {
    const key = rowFingerprint(row);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(row);
  }
  return out;
}

export function normalizeDataTableRows(rows: unknown): DataTableCell[][] {
  if (!Array.isArray(rows)) return [];
  const normalized = rows.map((row) =>
    Array.isArray(row) ? row.map((cell) => normalizeDataTableCell(cell)) : [normalizeDataTableCell(row)]
  );
  return dedupeDataTableRows(normalized);
}
